import React, { ReactNode, useEffect, useState } from "react";
import { Box, Tab, Tabs, SxProps, Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export interface GenericTabItem {
  /**
   * Text or component displayed in the tab button.
   */
  label: ReactNode;

  /**
   * Content displayed when this tab is selected.
   */
  content: ReactNode;

  /**
   * Optional stable identifier.
   */
  id?: string | number;

  /**
   * Prevents the tab from being selected.
   */
  disabled?: boolean;

  /**
   * Optional icon displayed in the tab.
   */
  icon?: React.ReactElement;

  /**
   * Controls where the icon is positioned relative to the label.
   */
  iconPosition?: "top" | "bottom" | "start" | "end";
}

interface GenericTabsProps {
  tabs: GenericTabItem[];

  /**
   * Use this to control the selected tab externally.
   * When omitted, GenericTabs manages its own selected tab.
   */
  value?: number;

  /**
   * Initial tab used when GenericTabs is uncontrolled.
   */
  defaultValue?: number;

  /**
   * Called whenever the selected tab changes.
   */
  onChange?: (newValue: number) => void;

  /**
   * Padding around the active tab content.
   */
  contentPadding?: number | string;

  /**
   * Makes the tabs take up the available width.
   */
  fullWidth?: boolean;

  /**
   * Allows the tab row to scroll horizontally.
   */
  scrollable?: boolean;

  /**
   * Keeps inactive tab content mounted but hidden.
   *
   * This can be useful when tab contents contain local state that should
   * not be reset when switching tabs.
   */
  keepMounted?: boolean;

  /**
   * Optional styling for the complete component.
   */
  sx?: SxProps<Theme>;

  /**
   * Optional styling for the Tabs navigation element.
   */
  tabsSx?: SxProps<Theme>;

  /**
   * Optional styling for the content container.
   */
  contentSx?: SxProps<Theme>;
}

const GenericTabs: React.FC<GenericTabsProps> = ({
  tabs,
  value,
  defaultValue = 0,
  onChange,
  contentPadding = 3,
  fullWidth = false,
  scrollable = false,
  keepMounted = false,
  sx,
  tabsSx,
  contentSx,
}) => {
  const theme = useTheme();

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);

  const selectedTab = isControlled ? value : internalValue;

  /*
   * Prevent the selected index from pointing to a tab that no longer exists
   * when the tabs array changes dynamically.
   */
  useEffect(() => {
    if (tabs.length === 0) return;

    if (selectedTab >= tabs.length) {
      const nextValue = tabs.length - 1;

      if (!isControlled) {
        setInternalValue(nextValue);
      }

      onChange?.(nextValue);
    }
  }, [tabs.length, selectedTab, isControlled, onChange]);

  const handleChange = (
    _event: React.SyntheticEvent,
    newValue: number
  ) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }

    onChange?.(newValue);
  };

  if (tabs.length === 0) {
    return null;
  }

  return (
    <Box sx={sx}>
      <Tabs
        value={selectedTab}
        onChange={handleChange}
        variant={scrollable ? "scrollable" : fullWidth ? "fullWidth" : "standard"}
        scrollButtons={scrollable ? "auto" : false}
        allowScrollButtonsMobile={scrollable}
        indicatorColor="primary"
        textColor="primary"
        aria-label="Page tabs"
        sx={{
            borderBottom: 1,
            borderColor: "divider",

            "& .MuiTabs-indicator": {
            backgroundColor:
                theme.custom?.modules?.community?.primary?.dark ??
                theme.palette.primary.main,
            },

            "& .MuiTab-root": {
            minHeight: 48,
            textTransform: "none",
            fontFamily: "inherit",
            fontSize: "inherit",
            fontWeight: 500,
            },

            "& .MuiTab-root.Mui-selected": {
            fontWeight: 500,
            },

            ...tabsSx,
        }}
        >
        {tabs.map((tab, index) => (
          <Tab
            key={tab.id ?? index}
            id={`generic-tab-${index}`}
            aria-controls={`generic-tab-panel-${index}`}
            label={tab.label}
            disabled={tab.disabled}
            icon={tab.icon}
            iconPosition={tab.iconPosition}
          />
        ))}
      </Tabs>

      <Box
        sx={{
          p: contentPadding,
          ...contentSx,
        }}
      >
        {tabs.map((tab, index) => {
          const isActive = selectedTab === index;

          if (!keepMounted && !isActive) {
            return null;
          }

          return (
            <Box
              key={tab.id ?? index}
              role="tabpanel"
              id={`generic-tab-panel-${index}`}
              aria-labelledby={`generic-tab-${index}`}
              hidden={!isActive}
              sx={{
                width: "100%",
              }}
            >
              {tab.content}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default GenericTabs;